import {
  Badge,
  Button,
  confirm,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import type { ProjectRiskListItem, RiskSeverity, RiskStatus } from "@/modules/management/project/domain/types";
import { managementProjectApi } from "../api/client";
import { useProjectRisks } from "../api/queries";
import { RiskFormDialog } from "./RiskFormDialog";

function severityLabel(s: RiskSeverity): string {
  switch (s) {
    case "high":
      return "ສູງ";
    case "medium":
      return "ກາງ";
    case "low":
      return "ຕ່ຳ";
    default:
      return s;
  }
}

function severityClass(s: RiskSeverity): string {
  switch (s) {
    case "high":
      return "bg-destructive/15 text-destructive border-destructive/30";
    case "medium":
      return "bg-yellow-500/15 text-yellow-700 dark:text-yellow-300 border-yellow-500/30";
    case "low":
      return "bg-muted text-muted-foreground";
    default:
      return "";
  }
}

function statusLabel(s: RiskStatus): string {
  switch (s) {
    case "open":
      return "ເປີດ";
    case "mitigated":
      return "ຫຼຸດຜ່ອນແລ້ວ";
    case "closed":
      return "ປິດ";
    default:
      return s;
  }
}

export function ProjectDetailRisksTab({
  projectId,
  canUpdate,
  openRiskDialogRequested = false,
  onOpenRiskDialogConsumed,
}: {
  projectId: string;
  canUpdate: boolean;
  openRiskDialogRequested?: boolean;
  onOpenRiskDialogConsumed?: () => void;
}) {
  const queryClient = useQueryClient();
  const risksQuery = useProjectRisks(projectId);
  const risks = risksQuery.data ?? [];
  const [riskDialog, setRiskDialog] = useState<{
    open: boolean;
    risk?: ProjectRiskListItem;
  }>({ open: false });

  useEffect(() => {
    if (openRiskDialogRequested && canUpdate) {
      setRiskDialog({ open: true });
      onOpenRiskDialogConsumed?.();
    }
  }, [openRiskDialogRequested, canUpdate, onOpenRiskDialogConsumed]);

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: ["management", "projects", projectId, "risks"],
    });
  };

  const handleDeleteRisk = async (risk: ProjectRiskListItem) => {
    const ok = await confirm({
      title: "ລຶບຄວາມສຽງ",
      description:
        "ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບຄວາມສຽງນີ້? ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້.",
      actionText: "ລຶບ",
      cancelText: "ຍົກເລີກ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      await managementProjectApi.deleteRisk(projectId, risk.id);
      toast.success("ລຶບຄວາມສຽງສຳເລັດ");
      invalidate();
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "ເກີດຂໍ້ຜິດພາດ",
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {canUpdate && (
          <Button
            size="sm"
            onClick={() => setRiskDialog({ open: true })}
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" />
            ເພີ່ມຄວາມສຽງ
          </Button>
        )}
      </div>
      <div className="rounded-xl border bg-card">
        {risksQuery.isLoading ? (
          <div className="py-8 text-center text-muted-foreground text-sm">
            ກຳລັງໂຫຼດ...
          </div>
        ) : risks.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground text-sm">
            ບໍ່ມີຄວາມສຽງ
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ລາຍລະອຽດ</TableHead>
                <TableHead>ລະດັບ</TableHead>
                <TableHead>ສະຖານະ</TableHead>
                {canUpdate && <TableHead className="w-[100px]" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {risks.map((risk) => (
                <TableRow key={risk.id}>
                  <TableCell className="font-medium">{risk.description}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={severityClass(risk.severity)}>
                      {severityLabel(risk.severity)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{statusLabel(risk.status)}</Badge>
                  </TableCell>
                  {canUpdate && (
                    <TableCell className="w-[100px]">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() =>
                            setRiskDialog({ open: true, risk })
                          }
                          aria-label="ແກ້ໄຂ"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteRisk(risk)}
                          aria-label="ລຶບ"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <RiskFormDialog
        projectId={projectId}
        risk={riskDialog.risk}
        open={riskDialog.open}
        onOpenChange={(open) => setRiskDialog((p) => ({ ...p, open }))}
        onSuccess={invalidate}
      />
    </div>
  );
}
